import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { PLAN_LABEL, PLAN_PRICING, getTrialStatus } from '../../utils/license';

// URLs de checkout da Kiwify (produtos de assinatura criados em 03/07/2026)
const CHECKOUT = {
  essencial: { monthly: 'https://pay.kiwify.com.br/ookQQa5', yearly: 'https://pay.kiwify.com.br/elmTcZ2' },
  completo:  { monthly: 'https://pay.kiwify.com.br/YxHT5tU', yearly: 'https://pay.kiwify.com.br/c616EOp' },
};

const fmt = v => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

const FEATURES = [
  { label: 'Contas, cartões e lançamentos ilimitados', e: true, c: true },
  { label: 'Categorias personalizadas e tags',          e: true, c: true },
  { label: 'Dívidas com baixa parcial',                 e: true, c: true },
  { label: 'Veículos com lembretes de manutenção',      e: true, c: true },
  { label: 'Metas e Orçamento Poderoso',                e: true, c: true },
  { label: 'Calendário e Resumo Anual',                 e: true, c: true },
  { label: 'Investimentos',                             e: true, c: true },
  { label: 'Notificações push de vencimento',           e: true, c: true },
  { label: 'Consultor IA — 20 análises com Gemini',     e: false, c: true },
  { label: 'Plano Família — multi-usuário em tempo real', e: false, c: true },
  { label: 'Relatório de Cartão de Crédito',            e: false, c: true },
  { label: 'Relatório de Imposto de Renda',             e: false, c: true },
  { label: 'WhatsApp com IA — lança por foto de comprovante', e: false, c: true },
];

export default function Plans() {
  const { state } = useApp();
  const [cycle, setCycle] = useState('yearly');
  const trial = getTrialStatus(state.user, state.license);
  const currentPlan = state.license?.plan;

  const yearlyMonthly = { essencial: PLAN_PRICING.essencial.yearly / 12, completo: PLAN_PRICING.completo.yearly / 12 };

  const PlanCard = ({ id, highlight }) => {
    const p = PLAN_PRICING[id];
    const isCurrent = currentPlan === id
      || (id === 'essencial' && currentPlan === 'basico')
      || (id === 'completo' && (currentPlan === 'pro' || currentPlan === 'familia'));
    return (
      <div className="card" style={{
        flex: '1 1 280px', maxWidth: 400, marginBottom: 0, position: 'relative', overflow: 'hidden',
        border: highlight ? '2px solid var(--gold)' : '1px solid var(--border)',
      }}>
        {highlight && (
          <div style={{ position: 'absolute', top: 14, right: -34, transform: 'rotate(45deg)', background: 'var(--gold)', color: '#0d1b2a', fontSize: 10, fontWeight: 800, padding: '3px 40px', letterSpacing: 1 }}>
            POPULAR
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 700, color: highlight ? 'var(--gold)' : 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
          {PLAN_LABEL[id]}
        </div>
        <div style={{ margin: '10px 0 2px' }}>
          <span style={{ fontSize: 34, fontWeight: 900, color: 'var(--text)' }}>
            R$ {fmt(cycle === 'monthly' ? p.monthly : yearlyMonthly[id])}
          </span>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>/mês</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          {cycle === 'yearly'
            ? <>cobrado R$ {fmt(p.yearly)}/ano — <strong style={{ color: 'var(--green)' }}>2 meses grátis</strong></>
            : 'cobrança mensal, cancele quando quiser'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {FEATURES.map(f => {
            const has = id === 'essencial' ? f.e : f.c;
            return (
              <div key={f.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: has ? 'var(--text2)' : 'var(--muted)', opacity: has ? 1 : 0.5 }}>
                <i className={`fas ${has ? 'fa-circle-check' : 'fa-circle-xmark'}`} style={{ color: has ? 'var(--green)' : 'var(--border)', marginTop: 2, fontSize: 13 }}></i>
                <span style={{ textDecoration: has ? 'none' : 'line-through' }}>{f.label}</span>
              </div>
            );
          })}
        </div>

        {isCurrent ? (
          <div style={{ textAlign: 'center', padding: 12, borderRadius: 12, background: 'rgba(29,158,117,0.1)', color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>
            <i className="fas fa-check" style={{ marginRight: 6 }}></i> Seu plano atual
          </div>
        ) : (
          <a
            href={CHECKOUT[id][cycle]}
            target="_blank"
            rel="noopener noreferrer"
            className={highlight ? 'btn btn-primary btn-full' : 'btn btn-ghost btn-full'}
            style={{ padding: 13, fontSize: 14, textAlign: 'center', display: 'block', textDecoration: 'none' }}
          >
            Assinar {PLAN_LABEL[id]}
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="page-enter">
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Planos</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
          {trial.active
            ? `Você está no período de teste gratuito — ${trial.daysLeft} dia${trial.daysLeft !== 1 ? 's' : ''} restante${trial.daysLeft !== 1 ? 's' : ''} com acesso completo`
            : currentPlan
              ? `Plano atual: ${PLAN_LABEL[currentPlan]}`
              : 'Escolha o plano ideal para suas finanças'}
        </div>
      </div>

      {/* Toggle mensal/anual */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <div style={{ display: 'inline-flex', background: 'var(--surface2)', borderRadius: 99, padding: 4, gap: 2 }}>
          {[['monthly', 'Mensal'], ['yearly', 'Anual — 2 meses grátis']].map(([id, label]) => (
            <button key={id} onClick={() => setCycle(id)} style={{
              padding: '8px 18px', borderRadius: 99, border: 'none', cursor: 'pointer',
              background: cycle === id ? 'var(--card-bg)' : 'transparent',
              color: cycle === id ? 'var(--text)' : 'var(--muted)',
              fontWeight: cycle === id ? 700 : 500, fontSize: 13,
              boxShadow: cycle === id ? 'var(--shadow)' : 'none',
              transition: 'all 0.15s',
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
        <PlanCard id="essencial" />
        <PlanCard id="completo" highlight />
      </div>

      {/* Nota para clientes vitalícios */}
      {['basico', 'pro', 'familia'].includes(currentPlan) && (
        <div style={{ marginTop: 20, maxWidth: 700, margin: '20px auto 0', background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <i className="fas fa-crown" style={{ color: 'var(--gold)', marginTop: 2 }}></i>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
            <strong>Você tem uma licença vitalícia</strong> — ela vale para sempre e já inclui os recursos do plano
            {currentPlan === 'basico' ? ' Essencial' : ' Completo'}. Nenhuma ação é necessária. Obrigado por acreditar no MAW desde o início! 💛
          </div>
        </div>
      )}
    </div>
  );
}
